import { httpInterceptor } from './http.interceptor';

export interface PushNotification {
  id: number;
  userId: number;
  email: string;
  title: string;
  message: string;
  path?: string;
  type?: string;
  data?: string;
  viewed: boolean;
  viewedAt?: Date;
  createdAt: Date;
}

export interface NotificationsResponse {
  notifications: PushNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UnviewedCountResponse {
  count: number;
}

export interface UserWithActiveFCM {
  user_id: number;
  user_codigo: number;
  user_nome_razao_social: string;
  user_role: string;
  user_cidade: string;
  user_estado: string;
}

export interface SendNotificationPayload {
  userIds: number[];
  title: string;
  message: string;
  path?: string;
}

export interface SendNotificationResponse {
  statusCode: number;
  success: boolean;
  message: string;
}

export interface UsersWithActiveFCMResponse {
  statusCode: number;
  success: boolean;
  data: UserWithActiveFCM[];
}

export interface AllNotificationsFilter {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
  viewed?: boolean;
}

export interface AllNotificationsResponse {
  statusCode: number;
  success: boolean;
  data: {
    notifications: PushNotification[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    filters: {
      startDate: string | null;
      endDate: string | null;
      type: string | null;
      viewed: boolean | null;
    };
  };
}

class PushNotificationService {
  async getNotifications(page: number = 1, limit: number = 10): Promise<NotificationsResponse> {
    try {
      const response = await httpInterceptor.get(
        `/push-notification/notifications?page=${page}&limit=${limit}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao buscar notificações');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      throw error;
    }
  }

  async getUnviewedCount(): Promise<UnviewedCountResponse> {
    try {
      const response = await httpInterceptor.get(
        '/push-notification/notifications-unviewed/count'
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao buscar contagem de notificações');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar contagem de notificações:', error);
      throw error;
    }
  }

  async markAsViewed(notificationId: number): Promise<void> {
    try {
      const response = await httpInterceptor.patch(
        `/push-notification/notifications/${notificationId}/viewed`,
        {}
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.message || 'Erro ao marcar notificação como visualizada');
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como visualizada:', error);
      throw error;
    }
  }

  async deleteNotification(notificationId: number): Promise<void> {
    try {
      const response = await httpInterceptor.delete(
        `/push-notification/notifications/${notificationId}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.message || 'Erro ao deletar notificação');
      }
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
      throw error;
    }
  }

  async getUsersWithActiveFCM(): Promise<UsersWithActiveFCMResponse> {
    try {
      const response = await httpInterceptor.get('/push-notification/users-with-active-fcm');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao buscar usuários com FCM ativo');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar usuários com FCM ativo:', error);
      throw error;
    }
  }

  async sendNotification(payload: SendNotificationPayload): Promise<SendNotificationResponse> {
    try {
      const response = await httpInterceptor.post('/push-notification/send-notification', payload);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao enviar notificação');
      }

      return data;
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      throw error;
    }
  }

  async getAllNotifications(filters?: AllNotificationsFilter): Promise<AllNotificationsResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.viewed !== undefined) params.append('viewed', filters.viewed.toString());

      const queryString = params.toString();
      const url = queryString
        ? `/push-notification/admin/all-notifications?${queryString}`
        : '/push-notification/admin/all-notifications';

      const response = await httpInterceptor.get(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao listar notificações');
      }

      return data;
    } catch (error) {
      console.error('Erro ao listar notificações:', error);
      throw error;
    }
  }
}

export const pushNotificationService = new PushNotificationService();
